import { NgIf } from '@angular/common';
import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { defer, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { PAYHOA_URLS } from '../../../../config/payhoa-urls';
import { StripeConnectService } from '../../../../services/stripe-treasury/stripe-connect.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { STRIPE_APPEARANCE_CONFIG } from '../../../v2-layout/stripe-treasury/stripe-treasury.constants';

export interface BankAccountStripeOnboardingDialogInput {
    organizationId: number;
}

export interface BankAccountStripeOnboardingDialogResult {
    exited: boolean;
    error?: boolean;
}

/**
 * Stripe's embedded Connect onboarding. Asked through `BankAccountStripeOnboardingDialogStateService`;
 * the flow host creates this component for each opening, never reopens a live one.
 *
 * Stripe's element is appended only once `createConnectAccount` has answered, by which time the
 * dialog has lifted itself to the body-level layer. Moving an iframe reloads it, so it must be born
 * where it stays.
 */
@UntilDestroy()
@Component({
    selector: 'app-bank-account-stripe-onboarding-dialog',
    templateUrl: './bank-account-stripe-onboarding-dialog.component.html',
    styleUrls: ['./bank-account-stripe-onboarding-dialog.component.scss'],
    standalone: true,
    imports: [NgIf],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BankAccountStripeOnboardingDialogComponent implements AfterViewInit, OnDestroy {
    @Input() open = false;
    @Input() organizationId: number;
    @Output() readonly closed = new EventEmitter<BankAccountStripeOnboardingDialogResult>();

    @ViewChild('stripeContainer') stripeContainer: ElementRef<HTMLDivElement>;

    loading = true;

    private _stripeConnectInstance: StripeConnectInstance | null = null;

    constructor(
        private _stripeConnectService: StripeConnectService,
        private _growler: GrowlerService,
    ) {}

    ngAfterViewInit(): void {
        this.initializeStripeConnect();
    }

    close(): void {
        this.closed.emit({ exited: true });
    }

    ngOnDestroy(): void {
        if (this._stripeConnectInstance) {
            this._stripeConnectInstance.logout();
            this._stripeConnectInstance = null;
        }
    }

    private initializeStripeConnect(): void {
        this._stripeConnectService.createConnectAccount({
            organizationId: this.organizationId,
        }).pipe(
            switchMap((response) =>
                defer(async () => loadConnectAndInitialize({
                    publishableKey: response.publishableKey,
                    fetchClientSecret: async () => response.clientSecret,
                    appearance: STRIPE_APPEARANCE_CONFIG,
                })),
            ),
            tap((stripeConnect) => {
                this._stripeConnectInstance = stripeConnect;
                this.mountOnboardingComponent(stripeConnect);
            }),
            catchError((error) => {
                this._growler.fromErrorResponse(error);
                this.closed.emit({ exited: true, error: true });
                return of(null);
            }),
            finalize(() => this.loading = false),
            untilDestroyed(this),
        ).subscribe();
    }

    private mountOnboardingComponent(stripeConnect: StripeConnectInstance): void {
        const onboardingComponent = stripeConnect.create('account-onboarding');

        onboardingComponent.setFullTermsOfServiceUrl(PAYHOA_URLS.TERMS_OF_SERVICE);
        onboardingComponent.setPrivacyPolicyUrl(PAYHOA_URLS.PRIVACY_POLICY);

        onboardingComponent.setCollectionOptions({
            fields: 'eventually_due',
            futureRequirements: 'include',
        });

        onboardingComponent.setOnStepChange((_stepChange) => {
            // Step change tracking — integrate with analytics when ready
        });

        onboardingComponent.setOnExit(() => this.close());

        if (this.stripeContainer?.nativeElement) {
            this.stripeContainer.nativeElement.appendChild(onboardingComponent);
        }
    }
}
